import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "wouter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "../lib/api";
import i18n from "../lib/i18n";

interface DocRef {
  id: string;
  slug: string;
  title: string;
  minRole: string;
}
interface Category {
  id: string;
  slug: string;
  title: string;
  pages: DocRef[];
}
interface TocResponse {
  data: Category[];
  meta: { locale: string; requested: string; translated: boolean };
}
interface DocPage {
  slug: string;
  title: string;
  body: string;
  updatedAt: string;
}
interface Hit {
  slug: string;
  title: string;
  excerpt: string;
}

/** In-app User Manual (stack §17a) — role-gated and searchable. */
export default function Manual() {
  const { t } = useTranslation();
  const params = useParams<{ slug?: string }>();
  const [term, setTerm] = useState("");
  const locale = i18n.resolvedLanguage ?? "en";

  // `api()` unwraps `data`, so fetch the envelope directly — the meta says whether
  // the reader is getting their own language or a fallback, and pretending
  // otherwise is how a half-translated manual goes unnoticed.
  const toc = useQuery({
    queryKey: ["docs", locale],
    queryFn: async () => {
      const res = await fetch(`/api/v1/docs?locale=${encodeURIComponent(locale)}`, {
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(String(res.status));
      return (await res.json()) as TocResponse;
    },
  });

  const page = useQuery({
    queryKey: ["docs", "page", params.slug, locale],
    queryFn: () => api<DocPage>(`/api/v1/docs/${params.slug}?locale=${encodeURIComponent(locale)}`),
    enabled: Boolean(params.slug),
  });

  const search = useQuery({
    queryKey: ["docs", "search", term, locale],
    queryFn: () =>
      api<Hit[]>(
        `/api/v1/docs/search?q=${encodeURIComponent(term)}&locale=${encodeURIComponent(locale)}`,
      ),
    enabled: term.trim().length > 1,
  });

  return (
    <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
      <aside>
        <div className="relative">
          <Label htmlFor="manual-search" className="sr-only">
            {t("common:searchManual")}
          </Label>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id="manual-search"
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={t("common:searchManual")}
            className="pl-9"
          />
        </div>

        {term.trim().length > 1 ? (
          <ul className="mt-4 space-y-2">
            {(search.data ?? []).map((hit) => (
              <li key={hit.slug}>
                <Link
                  href={`/manual/${hit.slug}`}
                  className="tap block rounded-md p-2 hover:bg-secondary/60"
                >
                  <span className="block text-sm font-medium">{hit.title}</span>
                  <span className="block text-xs text-muted-foreground">{hit.excerpt}</span>
                </Link>
              </li>
            ))}
            {search.isFetched && (search.data ?? []).length === 0 && (
              <li className="p-2 text-sm text-muted-foreground">{t("common:noResults")}</li>
            )}
          </ul>
        ) : (
          <nav className="mt-4 space-y-4" aria-label={t("navigation:manual")}>
            {(toc.data?.data ?? []).map((cat) => (
              <div key={cat.id}>
                <h3 className="text-xl font-semibold">{cat.title}</h3>
                <ul className="mt-1 space-y-0.5">
                  {cat.pages.map((p) => (
                    <li key={p.id}>
                      <Link
                        href={`/manual/${p.slug}`}
                        className={`tap block rounded-md px-2 py-1.5 text-sm hover:bg-secondary/60 ${
                          params.slug === p.slug ? "bg-secondary" : ""
                        }`}
                      >
                        {p.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        )}
      </aside>

      <article>
        {toc.data?.meta && !toc.data.meta.translated && (
          <p className="mb-4 rounded-md border border-[hsl(var(--chart-3)/0.45)] bg-[hsl(var(--chart-3)/0.10)] p-3 text-sm">
            {t("common:manualNotTranslated")}
          </p>
        )}
        {!params.slug && (
          <>
            <h1>{t("navigation:manual")}</h1>
            <p className="mt-2 text-muted-foreground">{t("common:manualIntro")}</p>
          </>
        )}
        {page.data && (
          <>
            <h1>{page.data.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("common:updated")} {new Date(page.data.updatedAt).toLocaleDateString(locale)}
            </p>
            <div className="mt-4 whitespace-pre-wrap leading-relaxed">{page.data.body}</div>
          </>
        )}
        {page.isError && <p className="text-destructive">{t("common:docUnavailable")}</p>}
      </article>
    </div>
  );
}
